import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Button,
  Link,
  VStack,
  HStack,
  Progress,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Alert,
  AlertIcon,
  PinInput,
  PinInputField,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ArrowBackIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { getPasswordStrength } from '../utils';
import { usePasswordReset, type PasswordResetStep } from '../hooks/usePasswordReset';

interface ForgotPasswordFormProps {
  // Theme colors
  formLabelColor: string;
  formFieldBg: string;
  formFieldBorder: string;
  formFieldBorderFocus: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  accentHover: string;
  // Handlers
  onBack: () => void;
  onSuccess?: () => void;
}

/**
 * Forgot Password Form Component
 *
 * Three-step flow:
 * 1. Request: User enters email or phone to receive verification code
 * 2. Verify: User enters the 4-digit code
 * 3. Reset: User sets new password
 *
 * Features:
 * - Choice between email or phone verification
 * - Password strength indicator
 * - Resend code option
 * - Success confirmation
 */
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  formLabelColor,
  formFieldBg,
  formFieldBorder,
  formFieldBorderFocus,
  textPrimary,
  textSecondary,
  textMuted,
  accentBlue,
  accentHover,
  onBack,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    step,
    identifier,
    identifierType,
    code,
    newPassword,
    confirmPassword,
    error,
    loading,
    setIdentifier,
    setIdentifierType,
    setCode,
    setNewPassword,
    setConfirmPassword,
    handleRequestReset,
    handleVerifyCode,
    handleResetPassword,
    handleResendCode,
    handleBack,
  } = usePasswordReset({
    onSuccess,
    onBack,
  });

  // Password strength for new password
  const passwordStrength = getPasswordStrength(newPassword);

  // Handle code input (combines 4 separate inputs into one value)
  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  // Step 1: Request - Enter email or phone
  const renderRequestStep = () => (
    <VStack spacing={4} align="stretch">
      <Text color={textSecondary} fontSize="sm">
        Enter your email or phone number and we'll send you a verification code.
      </Text>

      {/* Method selector */}
      <FormControl>
        <FormLabel color={formLabelColor}>Verify via</FormLabel>
        <RadioGroup
          value={identifierType}
          onChange={(value) => setIdentifierType(value as 'email' | 'phone')}
        >
          <Stack direction="row" spacing={4}>
            <Radio value="email" colorScheme="blue">
              <Text color={textPrimary}>Email</Text>
            </Radio>
            <Radio value="phone" colorScheme="blue">
              <Text color={textPrimary}>Phone</Text>
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>

      {/* Identifier input */}
      <FormControl isInvalid={!!error}>
        <FormLabel color={formLabelColor}>
          {identifierType === 'email' ? 'Email Address' : 'Phone Number'}
        </FormLabel>
        <Input
          type={identifierType === 'email' ? 'email' : 'tel'}
          placeholder={identifierType === 'email' ? 'your@email.com' : '+61 412 345 678'}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleRequestReset()}
          autoFocus
          bg={formFieldBg}
          color={textPrimary}
          borderColor={formFieldBorder}
          _placeholder={{ color: textMuted }}
          _focus={{ borderColor: formFieldBorderFocus }}
          _hover={{ borderColor: formFieldBorderFocus }}
        />
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>

      <Button
        bg={accentBlue}
        color="white"
        width="100%"
        onClick={handleRequestReset}
        isLoading={loading}
        _hover={{ bg: accentHover }}
      >
        Send Code
      </Button>

      <Button
        variant="ghost"
        width="100%"
        leftIcon={<ArrowBackIcon />}
        onClick={onBack}
        color={textSecondary}
      >
        Back to Sign In
      </Button>
    </VStack>
  );

  // Step 2: Verify - Enter the code
  const renderVerifyStep = () => (
    <VStack spacing={4} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          We sent a code to {identifier}. Enter it below.
        </Text>
      </Alert>

      <FormControl isInvalid={!!error}>
        <FormLabel color={formLabelColor} textAlign="center">
          Enter Verification Code
        </FormLabel>
        <HStack justify="center" spacing={4}>
          <PinInput
            value={code}
            onChange={handleCodeChange}
            size="lg"
            otp
          >
            <PinInputField
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _focus={{ borderColor: formFieldBorderFocus }}
            />
            <PinInputField
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _focus={{ borderColor: formFieldBorderFocus }}
            />
            <PinInputField
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _focus={{ borderColor: formFieldBorderFocus }}
            />
            <PinInputField
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _focus={{ borderColor: formFieldBorderFocus }}
            />
          </PinInput>
        </HStack>
        {error && <FormErrorMessage textAlign="center">{error}</FormErrorMessage>}
      </FormControl>

      <Button
        bg={accentBlue}
        color="white"
        width="100%"
        onClick={handleVerifyCode}
        isLoading={loading}
        isDisabled={code.length !== 4}
        _hover={{ bg: accentHover }}
      >
        Verify Code
      </Button>

      <Text textAlign="center" fontSize="sm" color={textSecondary}>
        Didn't receive the code?{' '}
        <Link color={accentBlue} onClick={handleResendCode}>
          Resend
        </Link>
      </Text>

      <Button
        variant="ghost"
        width="100%"
        leftIcon={<ArrowBackIcon />}
        onClick={handleBack}
        color={textSecondary}
      >
        Start Over
      </Button>
    </VStack>
  );

  // Step 3: Reset - Set new password
  const renderResetStep = () => (
    <VStack spacing={4} align="stretch">
      <Text color={textSecondary} fontSize="sm">
        Enter your new password below.
      </Text>

      {/* New Password */}
      <FormControl isInvalid={!!error && error.toLowerCase().includes('password')}>
        <FormLabel color={formLabelColor}>New Password</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            bg={formFieldBg}
            color={textPrimary}
            borderColor={formFieldBorder}
            _placeholder={{ color: textMuted }}
            _focus={{ borderColor: formFieldBorderFocus }}
            _hover={{ borderColor: formFieldBorderFocus }}
          />
          <InputRightElement>
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
              color={textMuted}
              _hover={{ color: textPrimary }}
            />
          </InputRightElement>
        </InputGroup>

        {/* Password strength indicator */}
        {newPassword.length > 0 && (
          <Box mt={2}>
            <Progress
              value={(passwordStrength.score / 4) * 100}
              size="xs"
              colorScheme={
                passwordStrength.score <= 1
                  ? 'red'
                  : passwordStrength.score === 2
                  ? 'yellow'
                  : 'green'
              }
              borderRadius="full"
            />
            <Text fontSize="xs" color={passwordStrength.color} mt={1}>
              Password strength: {passwordStrength.label}
            </Text>
          </Box>
        )}
      </FormControl>

      {/* Confirm Password */}
      <FormControl
        isInvalid={confirmPassword.length > 0 && newPassword !== confirmPassword}
      >
        <FormLabel color={formLabelColor}>Confirm Password</FormLabel>
        <InputGroup>
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
            autoComplete="new-password"
            bg={formFieldBg}
            color={textPrimary}
            borderColor={formFieldBorder}
            _placeholder={{ color: textMuted }}
            _focus={{ borderColor: formFieldBorderFocus }}
            _hover={{ borderColor: formFieldBorderFocus }}
          />
          <InputRightElement>
            <IconButton
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              color={textMuted}
              _hover={{ color: textPrimary }}
            />
          </InputRightElement>
        </InputGroup>
        {confirmPassword.length > 0 && newPassword !== confirmPassword && (
          <FormErrorMessage>Passwords do not match</FormErrorMessage>
        )}
      </FormControl>

      {error && !error.toLowerCase().includes('password') && (
        <Text color="red.500" fontSize="sm" textAlign="center">
          {error}
        </Text>
      )}

      <Button
        bg={accentBlue}
        color="white"
        width="100%"
        onClick={handleResetPassword}
        isLoading={loading}
        _hover={{ bg: accentHover }}
      >
        Reset Password
      </Button>
    </VStack>
  );

  // Step 4: Success
  const renderSuccessStep = () => (
    <VStack spacing={4} align="center" py={4}>
      <CheckCircleIcon boxSize={12} color="green.500" />
      <Text color={textPrimary} fontWeight="bold" fontSize="lg">
        Password Reset Successful
      </Text>
      <Text color={textSecondary} textAlign="center">
        Your password has been updated. You can now sign in with your new password.
      </Text>
      <Button
        bg={accentBlue}
        color="white"
        width="100%"
        onClick={onBack}
        _hover={{ bg: accentHover }}
      >
        Back to Sign In
      </Button>
    </VStack>
  );

  return (
    <Box>
      {step === 'request' && renderRequestStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'reset' && renderResetStep()}
      {step === 'success' && renderSuccessStep()}
    </Box>
  );
};
